import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { 
  Search, Eye, CheckCircle, Archive, Clock, X,
  ChevronRight, Mail, Phone, MapPin
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLeads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);
  const [filters, setFilters] = useState({
    type: "all",
    status: "all",
    search: ""
  });
  const [statusUpdate, setStatusUpdate] = useState({
    status: "",
    notes: ""
  });
  const [updating, setUpdating] = useState(false);

  const fetchLeads = useCallback(async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      let url = `${API}/admin/leads?limit=100`;
      
      if (filters.type !== "all") url += `&lead_type=${filters.type}`;
      if (filters.status !== "all") url += `&status=${filters.status}`;

      const response = await axios.get(url, { headers });
      setLeads(response.data.leads);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      } else {
        toast.error("Failed to load leads");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, filters.type, filters.status]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const fetchLeadDetail = async (leadId) => {
    const token = localStorage.getItem("admin_token");
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/leads/${leadId}`, { headers });
      setSelectedLead(response.data.lead);
      setAuditHistory(response.data.audit_history);
      setStatusUpdate({ status: response.data.lead.status, notes: "" });
      setShowDetail(true);
    } catch (error) {
      toast.error("Failed to load details");
    }
  };

  const updateLeadStatus = async () => {
    if (!selectedLead || !statusUpdate.status) return;
    
    const token = localStorage.getItem("admin_token");
    setUpdating(true);

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`${API}/admin/leads/${selectedLead.id}`, statusUpdate, { headers });
      toast.success(`Marked as ${statusUpdate.status}`);
      setShowDetail(false);
      fetchLeads();
    } catch (error) {
      toast.error("Update failed");
    } finally {
      setUpdating(false);
    }
  };

  const getLeadTypeLabel = (type) => {
    switch (type) {
      case "seller_property": return "Seller";
      case "builder_request": return "Builder Request";
      case "builder_buybox": return "Buy Box";
      case "contact": return "Contact";
      default: return type;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-50 text-amber-700 border-amber-200",
      reviewed: "bg-emerald-50 text-emerald-700 border-emerald-200",
      archived: "bg-zinc-100 text-zinc-600 border-zinc-200"
    };
    return styles[status] || styles.pending;
  };

  const filteredLeads = leads.filter(lead => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        lead.name?.toLowerCase().includes(search) ||
        lead.email?.toLowerCase().includes(search) ||
        lead.phone?.includes(search)
      );
    }
    return true;
  });

  return (
    <AdminLayout currentPage="/admin/leads">
      <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-leads">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-semibold text-[#18181B]">Leads</h1>
          <p className="text-sm text-[#71717A] mt-1">Review and manage submissions</p>
        </div>

        {/* Filters - Stack on mobile */}
        <div className="bg-white border border-[#E4E4E7] p-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
                <Input
                  placeholder="Search name, email, phone..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="pl-10 h-9 border-[#E4E4E7]"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <Select value={filters.type} onValueChange={(value) => setFilters(prev => ({ ...prev, type: value }))}>
                <SelectTrigger className="w-full sm:w-36 h-9 border-[#E4E4E7]" data-testid="filter-type">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="seller_property">Seller</SelectItem>
                  <SelectItem value="builder_request">Builder Request</SelectItem>
                  <SelectItem value="builder_buybox">Buy Box</SelectItem>
                  <SelectItem value="contact">Contact</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.status} onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}>
                <SelectTrigger className="w-full sm:w-36 h-9 border-[#E4E4E7]" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="reviewed">Reviewed</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Leads List */}
        <div className="bg-white border border-[#E4E4E7]">
          {loading ? (
            <div className="p-8 text-center text-[#71717A] text-sm">Loading...</div>
          ) : filteredLeads.length === 0 ? (
            <div className="p-8 text-center text-[#71717A] text-sm">No leads found</div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#71717A] uppercase tracking-wide">Type</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#71717A] uppercase tracking-wide">Name</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#71717A] uppercase tracking-wide">Contact</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#71717A] uppercase tracking-wide">Status</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#71717A] uppercase tracking-wide">Date</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#71717A] uppercase tracking-wide"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeads.map((lead) => (
                      <tr key={lead.id} className="border-b border-[#E4E4E7] last:border-0 hover:bg-[#FAFAFA]">
                        <td className="py-3 px-4 text-sm text-[#52525B]">{getLeadTypeLabel(lead.type)}</td>
                        <td className="py-3 px-4 text-sm font-medium text-[#18181B]">{lead.name}</td>
                        <td className="py-3 px-4">
                          <p className="text-sm text-[#52525B]">{lead.email}</p>
                          {lead.phone && <p className="text-xs text-[#A1A1AA]">{lead.phone}</p>}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded border ${getStatusBadge(lead.status)}`}>
                            {lead.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-[#A1A1AA] tabular-nums">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => fetchLeadDetail(lead.id)}
                            className="text-[#71717A] hover:text-[#18181B] p-1"
                            data-testid={`view-lead-${lead.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden divide-y divide-[#E4E4E7]">
                {filteredLeads.map((lead) => (
                  <div 
                    key={lead.id} 
                    className="p-4 active:bg-[#FAFAFA]"
                    onClick={() => fetchLeadDetail(lead.id)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#18181B] truncate">{lead.name}</p>
                        <p className="text-xs text-[#71717A] truncate">{lead.email}</p>
                      </div>
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded border ml-2 ${getStatusBadge(lead.status)}`}>
                        {lead.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#A1A1AA]">{getLeadTypeLabel(lead.type)}</span>
                      <div className="flex items-center gap-1 text-xs text-[#A1A1AA]">
                        {new Date(lead.created_at).toLocaleDateString()}
                        <ChevronRight className="w-3 h-3" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Lead Detail Dialog */}
        <Dialog open={showDetail} onOpenChange={setShowDetail}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0">
            <DialogHeader className="p-4 sm:p-6 pb-0">
              <DialogTitle className="text-lg font-semibold text-[#18181B]">
                Lead Details
              </DialogTitle>
            </DialogHeader>

            {selectedLead && (
              <div className="p-4 sm:p-6 pt-4 space-y-6">
                {/* Contact Info */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#F4F4F5] rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-[#52525B]">
                        {selectedLead.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-[#18181B]">{selectedLead.name}</p>
                      <p className="text-xs text-[#71717A]">{getLeadTypeLabel(selectedLead.type)}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-[#52525B]">
                      <Mail className="w-4 h-4 text-[#A1A1AA]" />
                      {selectedLead.email}
                    </div>
                    {selectedLead.phone && (
                      <div className="flex items-center gap-2 text-[#52525B]">
                        <Phone className="w-4 h-4 text-[#A1A1AA]" />
                        {selectedLead.phone}
                      </div>
                    )}
                    {selectedLead.property_address && (
                      <div className="flex items-start gap-2 text-[#52525B]">
                        <MapPin className="w-4 h-4 text-[#A1A1AA] mt-0.5" />
                        <span>
                          {selectedLead.property_address}<br />
                          {selectedLead.city}, {selectedLead.state} {selectedLead.zip}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Details */}
                <div className="space-y-3 text-sm">
                  {selectedLead.county && (
                    <div className="flex justify-between">
                      <span className="text-[#71717A]">County</span>
                      <span className="text-[#18181B]">{selectedLead.county}</span>
                    </div>
                  )}
                  {selectedLead.acres && (
                    <div className="flex justify-between">
                      <span className="text-[#71717A]">Acres</span>
                      <span className="text-[#18181B]">{selectedLead.acres}</span>
                    </div>
                  )}
                  {selectedLead.goal && (
                    <div className="flex justify-between">
                      <span className="text-[#71717A]">Goal</span>
                      <span className="text-[#18181B]">{selectedLead.goal}</span>
                    </div>
                  )}
                  {selectedLead.company && (
                    <div className="flex justify-between">
                      <span className="text-[#71717A]">Company</span>
                      <span className="text-[#18181B]">{selectedLead.company}</span>
                    </div>
                  )}
                  {selectedLead.markets_counties && (
                    <div>
                      <span className="text-[#71717A] block mb-1">Markets</span>
                      <span className="text-[#18181B]">{selectedLead.markets_counties}</span>
                    </div>
                  )}
                  {selectedLead.target_markets && (
                    <div>
                      <span className="text-[#71717A] block mb-1">Target Markets</span>
                      <span className="text-[#18181B]">{selectedLead.target_markets}</span>
                    </div>
                  )}
                  {selectedLead.message && (
                    <div>
                      <span className="text-[#71717A] block mb-1">Message</span>
                      <span className="text-[#18181B]">{selectedLead.message}</span>
                    </div>
                  )}
                  {selectedLead.known_issues && (
                    <div>
                      <span className="text-[#71717A] block mb-1">Known Issues</span>
                      <span className="text-[#18181B]">{selectedLead.known_issues}</span>
                    </div>
                  )}
                </div>

                {/* Timestamp */}
                <div className="flex items-center gap-2 text-xs text-[#A1A1AA] py-2 border-t border-[#E4E4E7]">
                  <Clock className="w-3 h-3" />
                  Submitted {new Date(selectedLead.created_at).toLocaleString()}
                </div>

                {/* Status Update */}
                <div className="space-y-3">
                  <Label className="text-xs font-medium text-[#71717A] uppercase tracking-wide">
                    Update Status
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {['pending', 'reviewed', 'archived'].map((status) => (
                      <button
                        key={status}
                        onClick={() => setStatusUpdate(prev => ({ ...prev, status }))}
                        className={`
                          px-3 py-1.5 text-xs font-medium rounded border transition-colors
                          ${statusUpdate.status === status 
                            ? 'bg-[#18181B] text-white border-[#18181B]' 
                            : 'bg-white text-[#52525B] border-[#E4E4E7] hover:border-[#A1A1AA]'
                          }
                        `}
                      >
                        {status === 'pending' && <Clock className="w-3 h-3 inline mr-1" />}
                        {status === 'reviewed' && <CheckCircle className="w-3 h-3 inline mr-1" />}
                        {status === 'archived' && <Archive className="w-3 h-3 inline mr-1" />}
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    ))}
                  </div>
                  <Textarea
                    placeholder="Add notes (optional)"
                    value={statusUpdate.notes}
                    onChange={(e) => setStatusUpdate(prev => ({ ...prev, notes: e.target.value }))}
                    rows={2}
                    className="border-[#E4E4E7] text-sm"
                  />
                </div>

                {/* Audit History */}
                {auditHistory.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-[#71717A] uppercase tracking-wide">
                      Audit Trail
                    </Label>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {auditHistory.map((audit, index) => (
                        <div key={index} className="text-xs text-[#71717A] bg-[#FAFAFA] p-2 rounded">
                          <span className="font-medium text-[#52525B]">{audit.admin_username}</span>
                          {' '}{audit.action.replace('status_change:', '').replace('→', ' → ')}{' '}
                          <span className="text-[#A1A1AA]">
                            {new Date(audit.timestamp).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="p-4 sm:p-6 pt-0 flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowDetail(false)}
                className="flex-1 sm:flex-none border-[#E4E4E7] text-[#52525B]"
              >
                Close
              </Button>
              <Button 
                onClick={updateLeadStatus} 
                disabled={updating}
                className="flex-1 sm:flex-none bg-[#18181B] hover:bg-[#27272A]"
              >
                {updating ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
